import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { OAuthEvent, OAuthService } from 'angular-oauth2-oidc';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';
import { authCodeFlowConfig } from '../configurations/auth.config';
import { AUTH_ACTIONS } from '../store/actions/auth.actions';
import { OAuthInfoEvent } from 'angular-oauth2-oidc/events';
import { EStorageMessages } from '../configurations/application.config';
import { APPLICATION_ACTIONS } from '../store/actions/application.actions';
import { LogService } from './log.service';

@Injectable({
  providedIn: 'root',
})
export class AuthFacadeService {
  constructor(
    private readonly oauthService: OAuthService,
    private readonly store: Store,
    private readonly logService: LogService,
  ) {
    this.oauthService.configure(authCodeFlowConfig);
    this.initEventsListener();
    void this.initLoginCheck();
  }

  private get isAuth(): boolean {
    return this.oauthService.hasValidIdToken() && this.oauthService.hasValidAccessToken();
  }

  logout(): void {
    this.store.dispatch(APPLICATION_ACTIONS.sendMessage({ message: EStorageMessages.logout }));
    void this.oauthService.revokeTokenAndLogout();
  }

  login(targetUrl?: string): void {
    this.oauthService.initCodeFlow(targetUrl);
  }

  setAutomaticRefreshToken(): void {
    this.oauthService.setupAutomaticSilentRefresh();
  }

  setSessionChecksIfStillLoggedIn(): void {
    this.oauthService.restartSessionChecksIfStillLoggedIn();
  }

  async refreshToken(): Promise<void> {
    await this.oauthService.silentRefresh();
  }

  async initLoginCheck(): Promise<void> {
    this.store.dispatch(AUTH_ACTIONS.initLoginCheck());
    await this.oauthService.loadDiscoveryDocumentAndTryLogin();
    this.setAutomaticRefreshToken();
  }

  private async loadUserProfile(): Promise<void> {
    const { info: userProfile } = (await this.oauthService.loadUserProfile()) as { info: IUserAuthProfile };
    this.store.dispatch(AUTH_ACTIONS.userProfileLoaded({ userProfile }));
  }

  private initEventsListener(): void {
    this.oauthService.events
      .pipe(
        map(() => this.isAuth),
        distinctUntilChanged(),
        tap((isAuthenticated) => this.authHandler(isAuthenticated)),
      )
      .subscribe();

    this.oauthService.events
      .pipe(
        tap((event) => this.logService.info(event, 'AuthFacadeService')),
        tap((event) => this.eventHandlers(event)),
      )
      .subscribe();
  }

  private authHandler(isAuthenticated: boolean): void {
    if (isAuthenticated) {
      this.store.dispatch(AUTH_ACTIONS.authenticated());
      this.store.dispatch(APPLICATION_ACTIONS.sendMessage({ message: EStorageMessages.login }));
    } else {
      this.store.dispatch(AUTH_ACTIONS.notAuthenticated());
    }
  }

  private eventHandlers(event: OAuthEvent): void {
    switch (event.type) {
      case 'discovery_document_loaded':
        this.onDiscoveryDocumentLoaded(event);
        break;
      case 'token_received':
        void this.loadUserProfile();
        break;
      case 'session_error':
      case 'session_terminated':
        this.onSessionTerminated();
        break;
    }
  }

  private onDiscoveryDocumentLoaded(event: OAuthEvent): void {
    const hasInfo = Boolean((event as OAuthInfoEvent).info);
    if (hasInfo && this.isAuth) {
      void this.loadUserProfile();
    }
    this.store.dispatch(AUTH_ACTIONS.discoveryDocumentLoaded());
  }

  private onSessionTerminated(): void {
    this.oauthService.stopAutomaticRefresh();
    this.store.dispatch(AUTH_ACTIONS.sessionTerminated());
  }
}

export interface IUserAuthProfile {
  amr: string[];
  at_hash: string;
  aud: string;
  auth_time: number; // in seconds
  exp: number; // in seconds
  family_name: string;
  given_name: string;
  iat: number; // in seconds
  idp: string;
  iss: string;
  name: string;
  nbf: number; // in seconds
  nonce: string;
  preferred_username: string;
  s_hash: string;
  sid: string;
  sub: string;
  'user-id': number;
  ExagoRole: string | string[];
}
